import { useEffect, useState } from 'react';
import { can, formatPercent, formatRial } from '@kia/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useApiResource } from '../lib/useApiResource';
import { Button, ErrorRetry, Field, Input, Modal, useToast } from '../ui';

interface BomLine {
  material_code: number | null;
  name: string;
  sepidar: string;
  qty: number;
  unit: string;
  unit_price: number | null;
  line_cost: number;
  source: string | null;
}
interface Bom {
  code: string;
  name: string;
  material: number;
  /** freight-in added to material (حمل ورودی) */ freight: number;
  /** absorbed labour + factory overhead (دستمزد و سربار) */ conversion: number;
  /** COGS = material + freight + conversion (بهای تمام‌شده) */ full: number;
  /** D6 scrap-cover doctrine lever — shown as a note; COGS itself is deliberately the point value */
  scrapPct: number;
  /** editable product master overlay — «نوع محصول» (WS-B, note #2) */ productType: string;
  confirmed: number;
  missingGU: string[];
  lines: BomLine[];
}

/** View/edit the product's «نوع محصول». Read-only for non-Admins; a datalist suggests existing types. */
function ProductTypeEditor({
  code,
  initial,
  canEdit,
  onSaved,
}: {
  code: string;
  initial: string;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [type, setType] = useState(initial);
  const [types, setTypes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setType(initial);
  }, [initial]);
  useEffect(() => {
    if (!canEdit) return;
    void api<{ types: string[] }>('/api/product-types').then((r) => {
      if (r.ok) setTypes((r.body as { types: string[] }).types);
    });
  }, [canEdit]);

  if (!canEdit) {
    return (
      <div className="totalbox">
        نوع محصول: <b>{initial || '—'}</b>
      </div>
    );
  }

  async function save() {
    setBusy(true);
    const r = await api(`/api/product/${encodeURIComponent(code)}/meta`, {
      method: 'POST',
      body: JSON.stringify({ productType: type }),
    });
    setBusy(false);
    if (r.ok) {
      toast('نوع محصول ذخیره شد');
      onSaved();
    } else {
      toast((r.body as { message?: string }).message ?? 'خطا در ذخیره', 'error');
    }
  }

  return (
    <div
      className="totalbox"
      style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}
    >
      <div style={{ flex: 1, minWidth: 200 }}>
        <Field label="نوع محصول">
          <Input
            value={type}
            onChange={(e) => setType(e.target.value)}
            list="product-type-list"
            maxLength={60}
            placeholder="مثلاً کولری، آسانسوری، هود…"
          />
          <datalist id="product-type-list">
            {types.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </Field>
      </div>
      <Button onClick={save} loading={busy}>
        ذخیرهٔ نوع
      </Button>
    </div>
  );
}

type ParamsHealthReason = 'ok' | 'pool_unset' | 'mix_empty' | 'all_eqkg_zero';

// The SPECIFIC missing input, in cost-inspector wording (the release gate says the same reason in its own
// release-context wording). Default covers any future reason so the message never silently disappears.
const PARAMS_UNHEALTHY_FA: Record<Exclude<ParamsHealthReason, 'ok'>, string> = {
  pool_unset: 'استخر تبدیل این ماه ثبت نشده',
  mix_empty: 'مبنای جذب سربار این ماه خالی است',
  all_eqkg_zero: 'محصولات مبنا وزن معادل مثبت ندارند',
};
const paramsUnhealthyFa = (r: ParamsHealthReason): string =>
  r === 'ok' ? '' : (PARAMS_UNHEALTHY_FA[r] ?? 'پارامترهای جذب این ماه معتبر نیست');

interface CostAsOf {
  date: string;
  costingYm: string;
  material: number;
  conversion: number;
  cogs: number;
  paramsHealthReason: ParamsHealthReason;
  exactShare: number;
  derivedShare: number;
  assumedShare: number;
}

/** «بها در تاریخ» — as-of-date COGS lookup (advisory time travel; anchors stay authoritative). */
function CostAsOfRow({ code }: { code: string }) {
  const [date, setDate] = useState('');
  const [res, setRes] = useState<CostAsOf | null>(null);
  const [busy, setBusy] = useState(false);

  async function lookup(d: string) {
    setDate(d);
    setRes(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
    setBusy(true);
    const r = await api<CostAsOf>(
      `/api/product/${encodeURIComponent(code)}/cost-asof?date=${encodeURIComponent(d)}`,
    );
    setBusy(false);
    if (r.ok) setRes(r.body as CostAsOf);
  }

  return (
    <div
      className="totalbox"
      style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
    >
      <Field label="بها در تاریخ">
        <Input type="date" value={date} onChange={(e) => void lookup(e.target.value)} />
      </Field>
      {busy ? (
        <span className="muted">در حال محاسبه…</span>
      ) : res ? (
        <span>
          بهای تمام‌شده در {res.date}: <b>{formatRial(Math.round(res.cogs))}</b>{' '}
          <span className="muted">
            (مواد {formatRial(Math.round(res.material))}
            {res.conversion > 0 ? <> + دستمزد {formatRial(Math.round(res.conversion))}</> : null}
            {res.paramsHealthReason !== 'ok' ? (
              <> · {paramsUnhealthyFa(res.paramsHealthReason)} — فقط مواد</>
            ) : null}
            {res.assumedShare > 0.01 ? (
              <> · {formatPercent(res.assumedShare)} بها بدون تاریخچهٔ قیمت — تقریبی</>
            ) : (
              <> · بر پایهٔ تاریخچهٔ قیمت‌ها</>
            )}
            )
          </span>
        </span>
      ) : null}
    </div>
  );
}

export function BomModal({ code, onClose }: { code: string; onClose: () => void }) {
  const { user } = useAuth();
  const canEdit = user ? can(user.role, 'config.write') : false;
  const {
    data: bom,
    status,
    error,
    reload,
  } = useApiResource<Bom>(`/api/product/${encodeURIComponent(code)}`, [code]);

  return (
    <Modal title={`BOM — ${bom?.name || code}`} onClose={onClose}>
      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : !bom ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : (
        <>
          <ProductTypeEditor
            code={code}
            initial={bom.productType}
            canEdit={canEdit}
            onSaved={() => reload()}
          />
          <div className="totalbox">
            بهای مواد: <b>{formatRial(bom.material)}</b>
            {bom.freight > 0 ? (
              <>
                {' '}
                · حمل ورودی: <b>{formatRial(bom.freight)}</b>
              </>
            ) : null}
            {bom.conversion > 0 ? (
              <>
                {' '}
                · دستمزد و سربار: <b>{formatRial(bom.conversion)}</b>
              </>
            ) : null}{' '}
            · بهای تمام‌شده: <b>{formatRial(bom.full)}</b> · ٪تأیید:{' '}
            <b>{formatPercent(bom.confirmed)}</b>
            {bom.scrapPct > 0 ? (
              <div className="muted">
                هزینه‌های پیش‌بینی‌نشده (حمل و…) تا ~{formatPercent(bom.scrapPct)} فروش با درآمد
                ضایعات پوشش داده می‌شود (در قیمت صفر فرض شده) — پوشش واقعی هر ماه در «بستن ماه».
              </div>
            ) : null}
            {bom.missingGU.length ? (
              <div className="warn">
                ⚠ گروه‌های بی‌قیمت:{' '}
                {[...new Set(bom.missingGU.map((k) => k.split('|')[0]))].join('، ')}
              </div>
            ) : null}
          </div>
          <CostAsOfRow code={code} />
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>کد ماده</th>
                  <th>قطعه</th>
                  <th>مقدار</th>
                  <th>واحد</th>
                  <th>قیمت واحد</th>
                  <th>بهای خط</th>
                </tr>
              </thead>
              <tbody>
                {bom.lines.map((l, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{l.material_code ?? '—'}</td>
                    <td>
                      {l.name}
                      {l.sepidar ? <div className="muted">سپیدار: {l.sepidar}</div> : null}
                    </td>
                    <td>{l.qty}</td>
                    <td>{l.unit}</td>
                    <td>{l.unit_price != null ? formatRial(l.unit_price) : '—'}</td>
                    <td>{formatRial(l.line_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
